import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Megaphone, 
  ArrowsClockwise, 
  Copy,
  CheckCircle,
  Target,
  Palette,
  Flask,
  Users,
  FilmStrip
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareResultButton } from '../components/ShareResultButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';
import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const objectives = ['Sales/Conversions', 'Lead Generation', 'Traffic', 'Brand Awareness', 'Engagement', 'App Installs'];
const funnelStages = ['Cold (Top of Funnel)', 'Warm (Middle of Funnel)', 'Hot (Bottom of Funnel)', 'Retargeting'];
const budgetRanges = ['$10-50/day', '$50-100/day', '$100-500/day', '$500+/day'];

export const MetaAdsPage = () => {
  const navigate = useNavigate();
  const [productService, setProductService] = usePersistedInput('meta-ads', 'productService', '');
  const [offer, setOffer] = usePersistedInput('meta-ads', 'offer', '');
  const [budgetRange, setBudgetRange] = usePersistedInput('meta-ads', 'budgetRange', '');
  const [objective, setObjective] = usePersistedInput('meta-ads', 'objective', '');
  const [audience, setAudience] = usePersistedInput('meta-ads', 'audience', '');
  const [funnelStage, setFunnelStage] = usePersistedInput('meta-ads', 'funnelStage', '');
  const [loading, setLoading] = useState(false);
  const [adsData, setAdsData] = usePersistedOutput('meta-ads', null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('create');

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.product_service) setProductService(inputs.product_service);
    if (inputs.offer) setOffer(inputs.offer);
    if (inputs.budget_range) setBudgetRange(inputs.budget_range);
    if (inputs.objective) setObjective(inputs.objective);
    if (inputs.audience) setAudience(inputs.audience);
    if (inputs.funnel_stage) setFunnelStage(inputs.funnel_stage);
    const output = item.output || {};
    if (output.ads) setAdsData(output.ads);
    setTab('create');
  };

  useEffect(() => {
    const prefill = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        const bp = data.brand_profile;
        if (bp) {
          if (bp.description) setProductService(prev => prev || bp.description);
          if (bp.target_audience) setAudience(prev => prev || bp.target_audience);
        }
      } catch (e) { /* ignore */ }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateAds = async () => {
    if (!productService || !offer || !budgetRange || !objective || !audience || !funnelStage) {
      setError('Please fill in all fields for the best results');
      return;
    }
    setError('');
    setLoading(true);
    setAdsData(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/meta-ads/generate`, {
        product_service: productService,
        offer,
        budget_range: budgetRange,
        objective,
        audience,
        funnel_stage: funnelStage
      });
      
      setAdsData(data.ads);
    } catch (err) {
      console.error('Failed to generate ads:', err);
      setError('Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <DashboardLayout>
      <div data-testid="meta-ads-page" className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <Megaphone size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Meta Ads Assistant</h1>
              <p className="text-zinc-400 text-sm">Plan and create ad campaigns</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {tab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
              <h2 className="font-medium font-['Instrument_Serif',serif]">Campaign Settings</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Product / Service</label>
                <input
                  type="text"
                  value={productService}
                  onChange={(e) => setProductService(e.target.value)}
                  data-testid="product-input"
                  placeholder="e.g., Organic skincare serum"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Offer</label>
                <input
                  type="text"
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  data-testid="offer-input"
                  placeholder="e.g., 20% off first order"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Budget Range</label>
                <Select value={budgetRange} onValueChange={setBudgetRange}>
                  <SelectTrigger data-testid="budget-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {budgetRanges.map((b) => (
                      <SelectItem key={b} value={b} className="text-white hover:bg-white/10">
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Campaign Objective</label>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger data-testid="objective-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select objective" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {objectives.map((o) => (
                      <SelectItem key={o} value={o} className="text-white hover:bg-white/10">
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Audience</label>
                <input
                  type="text"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="audience-input"
                  placeholder="e.g., Women 25-45, interested in beauty"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Funnel Stage</label>
                <Select value={funnelStage} onValueChange={setFunnelStage}>
                  <SelectTrigger data-testid="funnel-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {funnelStages.map((s) => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-white/10">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                onClick={generateAds}
                disabled={loading || !productService || !offer || !budgetRange || !objective || !audience || !funnelStage}
                data-testid="generate-ads-btn"
                className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <ArrowsClockwise size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Megaphone size={18} />
                    Generate Ad Strategy
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {!adsData ? (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="text-center py-16">
                  <Megaphone size={48} className="mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-medium mb-2">No ad strategy generated yet</h3>
                  <p className="text-sm">Fill in your campaign details and generate your strategy</p>
                </div>
              </div>
            ) : (
              <>
                {/* Strategy */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Target size={20} className="text-blue-400" />
                    <h2 className="font-medium font-['Instrument_Serif',serif]">Campaign Strategy</h2>
                    <div className="ml-auto">
                      <ShareResultButton
                        title="Meta Ads Strategy"
                        content={adsData.strategy?.slice(0, 300) || ''}
                        type="Ad Strategy"
                      />
                    </div>
                  </div>
                  <p className="text-zinc-300">{adsData.strategy}</p>
                </div>

                {/* Hooks */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Hook Ideas</h2>
                  <div className="space-y-3">
                    {adsData.hooks?.map((hook, index) => (
                      <div key={index} className="flex items-center justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-200">"{hook}"</span>
                        <button
                          onClick={() => copyToClipboard(hook, `hook-${index}`)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === `hook-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ad Variations */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Ad Copy Variations</h2>
                  <div className="space-y-4">
                    {adsData.ad_variations?.map((ad, index) => (
                      <div key={index} className="bg-zinc-800/50 border border-white/10 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 mb-3 inline-block">
                              Variation {index + 1}
                            </span>
                            <p className="text-zinc-200 text-sm mb-3 whitespace-pre-line">{ad.primary_text}</p>
                            <div className="flex items-center gap-4">
                              <div>
                                <span className="text-xs text-zinc-500">Headline:</span>
                                <p className="text-white font-medium">{ad.headline}</p>
                              </div>
                              <div>
                                <span className="text-xs text-zinc-500">CTA:</span>
                                <p className="text-white">{ad.cta}</p>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(`${ad.primary_text}\n\nHeadline: ${ad.headline}\nCTA: ${ad.cta}`, `ad-${index}`)}
                            className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            {copiedKey === `ad-${index}` ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Create Video Ad CTA */}
                {adsData.ad_variations?.length > 0 && (
                  <div className="bg-gradient-to-r from-rose-500/10 to-orange-500/10 border border-orange-500/20 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium font-['Instrument_Serif',serif] flex items-center gap-2">
                          <FilmStrip size={20} className="text-orange-400" />
                          Turn this into a video ad
                        </h3>
                        <p className="text-zinc-400 text-sm mt-1">Create an written for you video ad from your best copy</p>
                      </div>
                      <button
                        onClick={() => {
                          const bestAd = adsData.ad_variations[0];
                          const params = new URLSearchParams({
                            copy: bestAd.primary_text || '',
                            headline: bestAd.headline || '',
                            product: productService || ''
                          });
                          navigate(`/dashboard/ad-creator?${params.toString()}`);
                        }}
                        data-testid="create-video-from-ads"
                        className="px-5 py-2.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full font-medium text-sm hover:from-rose-600 hover:to-orange-600 transition-all flex items-center gap-2 whitespace-nowrap"
                      >
                        <FilmStrip size={16} />
                        Create Video Ad
                      </button>
                    </div>
                  </div>
                )}

                {/* Audience & Creative */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Users size={20} className="text-green-400" />
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Audience Suggestions</h2>
                    </div>
                    <ul className="space-y-2">
                      {adsData.audience_suggestions?.map((item, index) => (
                        <li key={index} className="text-zinc-300 text-sm flex items-start gap-2">
                          <span className="text-green-400">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette size={20} className="text-purple-400" />
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Creative Directions</h2>
                    </div>
                    <ul className="space-y-2">
                      {adsData.creative_directions?.map((item, index) => (
                        <li key={index} className="text-zinc-300 text-sm flex items-start gap-2">
                          <span className="text-purple-400">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Testing Ideas */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Flask size={20} className="text-yellow-400" />
                    <h2 className="font-medium font-['Instrument_Serif',serif]">A/B Testing Ideas</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {adsData.testing_ideas?.map((idea, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="w-6 h-6 rounded-full bg-yellow-500/10 flex items-center justify-center text-xs font-medium text-yellow-400">{index + 1}</span>
                        <span className="text-zinc-300 text-sm">{idea}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Regenerate */}
                <button
                  onClick={generateAds}
                  disabled={loading}
                  className="w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
                  Regenerate Strategy
                </button>
              </>
            )}
          </motion.div>
        </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Meta Ads History</h2>
            <TextHistoryPanel toolType="meta_ads" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MetaAdsPage;
